import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { EntityReducers } from '@core/store/EntityReducer';
import { FilterType, ISitePhoto, PagedResponseModel, SiteSummaryModel, SiteSummaryPagedRequest } from '@shared/models';
import httpClient from '@core/http/httpClient';
import { applicationPaths } from '@core/http/applicationPaths';
import { ITEMS_PER_PAGE } from '@shared/components/Sidebar/SiteList/InfiniteScrollList';
import { getPathPayload, omitAbortSignalConfig } from '@hwm/ui-lib';

const getSiteList = createAsyncThunk(
  `${EntityReducers.sites}/getSiteList`,
  async (payload: SiteSummaryPagedRequest) => {
    const response: AxiosResponse<PagedResponseModel<SiteSummaryModel>> = await httpClient.get(
      applicationPaths.sites.siteList,
      {
        params: {
          ...payload,
          filterType: FilterType.All,
          pageSize: ITEMS_PER_PAGE,
        } as SiteSummaryPagedRequest,
        signal: omitAbortSignalConfig,
      }
    );

    return response.data;
  }
);

const getSelectedSiteList = createAsyncThunk(
  `${EntityReducers.sites}/getSelectedSiteList`,
  async (payload: SiteSummaryPagedRequest) => {
    const response: AxiosResponse<PagedResponseModel<SiteSummaryModel>> = await httpClient.get(
      applicationPaths.sites.siteList,
      {
        params: {
          ...payload,
          filterType: FilterType.Selected,
          pageSize: ITEMS_PER_PAGE,
        } as SiteSummaryPagedRequest,
        signal: omitAbortSignalConfig,
      }
    );

    return response.data;
  }
);

const handleFavorites = createAsyncThunk(
  `${EntityReducers.sites}/handleFavorites`,
  async ({ add, siteId }: { add: boolean; siteId: number }) => {
    const url = applicationPaths.sites.selectedSite({ siteId: siteId?.toString() });

    const response: AxiosResponse = await (add ? httpClient.put(url) : httpClient.delete(url));

    const res = { ...response, siteId, add } as AxiosResponse<any> & {
      siteId: number;
      add: boolean;
    };

    return res;
  }
);

const uploadPhotos = createAsyncThunk(
  `${EntityReducers.sites}/uploadPhotos`,
  async ({
    pathParams,
    body,
  }: {
    pathParams: getPathPayload<typeof applicationPaths.siteInformation.photos>;
    body: File[];
  }) => {
    const promises = body.map(async (file) => {
      const bodyFormData = new FormData();
      bodyFormData.append('file', file);
      bodyFormData.append('description', file.name);
      return httpClient.post<ISitePhoto>(`${applicationPaths.siteInformation.photos(pathParams)}`, bodyFormData);
    });

    await Promise.all(promises);
  }
);

export const thunks = {
  getSiteList,
  getSelectedSiteList,
  handleFavorites,
  uploadPhotos,
};
